// Portfolio Administration v1.0 PR 5 — shared list-page primitive for the
// cross-portfolio Vendors + Local Enforcers registry pages. Captures the
// repeated structure (page title / actions / filter bar / count / table)
// so each entity owns only its column definitions + filter chips.

import type { ReactNode } from "react";
import { Card } from "@/design-system/components/ui";

export interface RegistryListLayoutProps {
  title: string;
  testIdPrefix: string;
  /** Buttons rendered to the right of the title (e.g. "+ Add"). */
  actions?: ReactNode;
  /** Optional toggle/tab strip between the title and filter row. */
  toolbar?: ReactNode;
  /** Multi-line filter row content. */
  filters: ReactNode;
  /** Active filter chip strip (optional). */
  activeFilters?: ReactNode;
  /** Bulk-action bar (rendered above the table when rows are selected). */
  bulkActions?: ReactNode;
  /** Row count summary, e.g. "Showing 12 of 47 vendors". */
  countSummary: ReactNode;
  /** Table or empty state. */
  children: ReactNode;
}

export function RegistryListLayout({
  title,
  testIdPrefix,
  actions,
  toolbar,
  filters,
  activeFilters,
  bulkActions,
  countSummary,
  children,
}: RegistryListLayoutProps) {
  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid={`${testIdPrefix}-list-page`}
    >
      <div className="flex items-center justify-between gap-space-3">
        <h1 className="font-display font-bold text-h1 m-0">{title}</h1>
        {actions ? (
          <div className="flex items-center gap-space-2">{actions}</div>
        ) : null}
      </div>

      {toolbar ? <div>{toolbar}</div> : null}

      <Card className="flex flex-col gap-space-3">
        <div
          className="flex flex-wrap items-end gap-space-3"
          data-testid={`${testIdPrefix}-list-filters`}
        >
          {filters}
        </div>
        {activeFilters ? (
          <div
            className="flex flex-wrap items-center gap-space-1"
            data-testid={`${testIdPrefix}-active-filters`}
          >
            {activeFilters}
          </div>
        ) : null}
      </Card>

      {bulkActions ? <div>{bulkActions}</div> : null}

      <div className="flex items-center justify-between">
        <p
          className="text-small text-gray-mid m-0"
          data-testid={`${testIdPrefix}-count-summary`}
        >
          {countSummary}
        </p>
      </div>

      {children}
    </div>
  );
}
